import React, { useEffect, useRef } from 'react';
import { useSignalStore } from '../store';
import { signalsAPI } from '../services/api';
import './Signals.css';

function Signals() {
  const signals = useSignalStore((state) => state.signals);
  const setSignals = useSignalStore((state) => state.setSignals);
  const intervalRef = useRef(null);
  const abortRef = useRef(null);
  const [scanning, setScanning] = React.useState(false);

  useEffect(() => {
    fetchSignals();
    
    // Auto-refresh every 30 seconds
    intervalRef.current = setInterval(() => {
      fetchSignals();
    }, 30000);

    // Cleanup interval and pending request on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const fetchSignals = async () => {
    // Cancel any in-flight request before starting a new one
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    try {
      const response = await signalsAPI.list({ limit: 100 }, { signal: abortRef.current.signal });
      setSignals(response.data);
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch signals:', error);
    }
  };

  const handleManualScan = async () => {
    setScanning(true);
    try {
      await signalsAPI.scan();
      // Wait a few seconds for scan to generate results, then refresh
      setTimeout(async () => {
        await fetchSignals();
        setScanning(false);
      }, 10000);
    } catch (error) {
      console.error('Failed to trigger scan:', error);
      setScanning(false);
    }
  };

  return (
    <div className="signals">
      <div className="signals-header">
        <h2>Trading Signals</h2>
        <button
          className="btn-scan"
          onClick={handleManualScan}
          disabled={scanning}
        >
          {scanning ? 'Scanning...' : 'Scan Now'}
        </button>
      </div>
      
      {signals.length === 0 ? (
        <div className="empty-state">
          <p>No signals generated yet.</p>
          <p className="hint">Create and activate a strategy to start receiving signals.</p>
        </div>
      ) : (
        <div className="signals-list">
          {signals.map((signal) => (
            <div key={signal.id} className={`signal-item signal-${signal.signal_type.toLowerCase()}`}>
              <div className="signal-header">
                <span className={`signal-badge signal-${signal.signal_type.toLowerCase()}`}>
                  {signal.signal_type}
                </span>
                <h4>{signal.symbol}</h4>
                {signal.indicators?.quality_score != null && (
                  <span className={`quality-badge quality-${signal.indicators.quality_grade?.toLowerCase() || 'd'}`}>
                    {signal.indicators.quality_grade} ({signal.indicators.quality_score})
                  </span>
                )}
                <span className="confidence">
                  {Math.round(signal.confidence * 100)}% confidence
                </span>
              </div>
              {signal.strategy_name && (
                <div className="signal-strategy">
                  <span className="strategy-tag">{signal.strategy_name}</span>
                </div>
              )}
              <div className="signal-body">
                <p><strong>Price:</strong> ${signal.price}</p>
                <p><strong>Time:</strong> {new Date(signal.created_at).toLocaleString()}</p>
                {(signal.signal_type === 'BUY' || signal.signal_type === 'WATCHLIST') && signal.indicators?.trade_plan && (
                  <div className="trade-plan">
                    <p><strong>Stop Loss:</strong> <span className="stop-price">${signal.indicators.trade_plan.stop_price}</span></p>
                    <p><strong>Take Profit:</strong> <span className="target-price">${signal.indicators.trade_plan.target_price}</span></p>
                  </div>
                )}
                {signal.indicators?.quality_reasons && signal.indicators.quality_reasons.length > 0 && (
                  <div className="quality-reasons">
                    <strong>Quality Breakdown:</strong>
                    <ul>
                      {signal.indicators.quality_reasons.map((reason, i) => (
                        <li key={i}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {signal.reasoning && (
                  <p><strong>Reasoning:</strong> {signal.reasoning}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Signals;
